import { useEffect, useRef, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { CreateAccountLayout } from './layout';
import { CreateAccountForm } from './form';
import { PageMeta } from '../../components/PageMeta';
import { ContinueAs } from '../../components/ContinueAs';
import { TermsLine } from '../../components/TopBar';
import { createAccountAtom } from '../../state';
import { checkVisitor, shouldCheckVisitor, type Visitor } from '../../signedInVisitor';
import { confirmAs, signedInAtom } from '../../session';
import { leaveSignup } from '../../leave';
import { signOut } from '../../auth';

export const CreateAccount = () => {
    const navigate = useNavigate();
    // Back from the code step: mid-signup, not a visit.
    const started = !!useAtomValue(createAccountAtom);
    const setSignedIn = useSetAtom(signedInAtom);
    const [visitor, setVisitor] = useState<Visitor>({ kind: 'anonymous' });
    const checked = useRef(false);

    useEffect(() => {
        // Once, for the visit (not again once they start typing).
        if (checked.current || !shouldCheckVisitor(started)) return;
        checked.current = true;
        // No cancel on unmount: StrictMode's second run is the ref's, and the answer must land.
        void checkVisitor().then((v) => {
            if (v.kind !== 'anonymous') setSignedIn({ who: v.who, ...(v.kind === 'has-org' ? { org: v.org } : {}) });
            setVisitor(v);
        });
    }, [started, setSignedIn]);

    return (
        <>
            <PageMeta
                title="Sign up · Dodil Cloud"
                description="Create your Dodil Cloud account — free to start, no credit card."
            />
            <CreateAccountLayout>
                {visitor.kind === 'anonymous' ? (
                    <>
                        <CreateAccountForm />
                        <TermsLine />
                    </>
                ) : (
                    // Signed in already: who, before anything is done for them.
                    <ContinueAs
                        who={visitor.who}
                        onContinue={() => {
                            confirmAs(visitor.who);
                            if (visitor.kind === 'has-org') leaveSignup();
                            else navigate('/signup/org');
                        }}
                        onSomeoneElse={() => { void signOut(); }}
                    />
                )}
            </CreateAccountLayout>
        </>
    );
};
